import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
  MenuItem,
  Switch,
  FormControlLabel,
  LinearProgress,
  Alert,
} from '@mui/material';
import PublishIcon from '@mui/icons-material/Publish';
import EducatorLayout from './components/EducatorLayout';
import { listSubjects } from '../api/subjects';
import { listGradeSections } from '../api/gradeSections';
import { createLesson, listLessons, updateLesson } from '../api/lessons';

const EducatorLessons = () => {
  const [subjects, setSubjects] = useState([]);
  const [sections, setSections] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [editingLessonId, setEditingLessonId] = useState(null);
  const [form, setForm] = useState({
    title: '',
    description: '',
    objectives: '',
    videoUrl: '',
    videoFile: null,
    educatorFeedback: '',
    subjectId: '',
    grade: '',
    sectionId: '',
    availabilityStart: null,
    availabilityEnd: null,
    isVisible: true,
    files: [],
    quizQuestions: [],
  });

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [subj, sect] = await Promise.all([
          listSubjects(),
          listGradeSections(),
        ]);
        setSubjects(subj || []);
        setSections(sect || []);
        const recent = await listLessons();
        setLessons(Array.isArray(recent) ? recent.slice(0, 20) : []);
      } catch (err) {
        setError(err?.response?.data?.message || 'Failed to load references');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (field === 'subjectId') {
      const subj = subjects.find((s) => s._id === value);
      if (subj?.grade) setForm((prev) => ({ ...prev, grade: subj.grade }));
    }
  };

  const handleFileSelect = (evt) => {
    const files = Array.from(evt.target.files || []);
    if (files.length) {
      setForm((prev) => ({ ...prev, files }));
    }
  };

  const handleVideoSelect = (evt) => {
    const file = evt.target.files?.[0] || null;
    setForm((prev) => ({ ...prev, videoFile: file }));
  };

  const formatDateInput = (value) => {
    if (!value) return '';
    try {
      return new Date(value).toISOString().slice(0, 16);
    } catch {
      return '';
    }
  };

  const resetForm = () => {
    setForm({
      title: '',
      description: '',
      objectives: '',
      videoUrl: '',
      videoFile: null,
      educatorFeedback: '',
      subjectId: '',
      grade: '',
      sectionId: '',
      availabilityStart: null,
      availabilityEnd: null,
      isVisible: true,
      files: [],
      quizQuestions: [],
    });
    setEditingLessonId(null);
  };
  const loadLessonToForm = (lesson, mode = 'edit') => {
    const quizQuestions = Array.isArray(lesson?.quiz?.config?.questions)
      ? lesson.quiz.config.questions
      : [];
    setForm({
      title: mode === 'reuse' ? `Copy of ${lesson.title}` : lesson.title || '',
      description: lesson.description || '',
      objectives: Array.isArray(lesson.objectives) ? lesson.objectives.join('\n') : '',
      videoUrl: lesson.videoUrl || '',
      videoFile: null,
      educatorFeedback: lesson.educatorFeedback || '',
      subjectId: lesson.subjectId?._id || lesson.subjectId || '',
      grade: lesson.grade || '',
      sectionId: lesson.sectionId?._id || lesson.sectionId || '',
      availabilityStart: formatDateInput(lesson.availabilityStart),
      availabilityEnd: formatDateInput(lesson.availabilityEnd),
      isVisible: lesson.isVisible !== false,
      files: [],
      quizQuestions,
    });
    setEditingLessonId(mode === 'edit' ? lesson._id : null);
  };

  const updateQuestion = (index, changes) => {
    setForm((prev) => {
      const next = [...prev.quizQuestions];
      next[index] = { ...next[index], ...changes };
      return { ...prev, quizQuestions: next };
    });
  };

  const addQuestion = (type = 'mcq') => {
    setForm((prev) => ({
      ...prev,
      quizQuestions: [
        ...prev.quizQuestions,
        type === 'short'
          ? { type: 'short', prompt: '', answer: '' }
          : { type: 'mcq', prompt: '', options: ['', ''], correctIndex: 0 },
      ],
    }));
  };

  const removeQuestion = (index) => {
    setForm((prev) => ({
      ...prev,
      quizQuestions: prev.quizQuestions.filter((_, idx) => idx !== index),
    }));
  };

  const addOption = (qIndex) => {
    setForm((prev) => {
      const next = [...prev.quizQuestions];
      const target = next[qIndex];
      if (!target || target.type !== 'mcq') return prev;
      target.options = [...(target.options || []), ''];
      return { ...prev, quizQuestions: next };
    });
  };

  const updateOption = (qIndex, optIndex, value) => {
    setForm((prev) => {
      const next = [...prev.quizQuestions];
      const target = next[qIndex];
      if (!target || target.type !== 'mcq') return prev;
      const options = [...(target.options || [])];
      options[optIndex] = value;
      target.options = options;
      return { ...prev, quizQuestions: next };
    });
  };

  const removeOption = (qIndex, optIndex) => {
    setForm((prev) => {
      const next = [...prev.quizQuestions];
      const target = next[qIndex];
      if (!target || target.type !== 'mcq') return prev;
      target.options = (target.options || []).filter((_, idx) => idx !== optIndex);
      if (target.correctIndex >= target.options.length) {
        target.correctIndex = Math.max(0, target.options.length - 1);
      }
      return { ...prev, quizQuestions: next };
    });
  };

  const handleSubmit = async (evt) => {
    evt.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const quizQuestions = (form.quizQuestions || [])
        .map((q) => {
          if (!q?.prompt?.trim()) return null;
          if (q.type === 'short') {
            return { type: 'short', prompt: q.prompt.trim(), answer: q.answer || '' };
          }
          const options = (q.options || []).map((opt) => String(opt || '').trim()).filter(Boolean);
          return { type: 'mcq', prompt: q.prompt.trim(), options, correctIndex: Number(q.correctIndex) || 0 };
        })
        .filter(Boolean);
      const quiz = quizQuestions.length
        ? { type: 'embedded', config: { questions: quizQuestions } }
        : { type: 'none', config: {} };
      const payload = {
        ...form,
        objectives: form.objectives ? form.objectives.split('\n').filter(Boolean) : [],
        quiz,
        availabilityStart: form.availabilityStart || null,
        availabilityEnd: form.availabilityEnd || null,
        sectionId: form.sectionId || null,
      };
      if (editingLessonId) {
        await updateLesson(editingLessonId, payload);
      } else {
        await createLesson(payload);
      }
      const fresh = await listLessons();
      setLessons(Array.isArray(fresh) ? fresh.slice(0, 20) : []);
      resetForm();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to save lesson');
    } finally {
      setSubmitting(false);
    }
  };

  const editingLesson = lessons.find((lesson) => lesson._id === editingLessonId);

  return (
    <EducatorLayout>
      <Box>
        {(loading || submitting) && <LinearProgress />}
        {error && <Alert severity="error">{error}</Alert>}

        <Card elevation={1} sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
            <Stack spacing={3}>
              <Typography variant="h6" fontWeight={700}>
                Lesson Builder
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} md={7}>
                  <Card elevation={1} sx={{ borderRadius: 3, height: '100%' }}>
                    <CardHeader title="Create lesson" />
                    <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                      <Stack spacing={2} component="form" onSubmit={handleSubmit}>
                        <TextField
                          label="Title"
                          value={form.title}
                          onChange={(e) => handleChange('title', e.target.value)}
                          required
                          fullWidth
                        />
                        <TextField
                          label="Description"
                          value={form.description}
                          onChange={(e) => handleChange('description', e.target.value)}
                          multiline
                          minRows={2}
                        />
                        <TextField
                          label="Educator feedback (visible to students)"
                          value={form.educatorFeedback}
                          onChange={(e) => handleChange('educatorFeedback', e.target.value)}
                          multiline
                          minRows={2}
                        />
                        <TextField
                          label="Objectives (one per line)"
                          value={form.objectives}
                          onChange={(e) => handleChange('objectives', e.target.value)}
                          multiline
                          minRows={3}
                        />
                        <TextField
                          select
                          label="Subject"
                          value={form.subjectId}
                          onChange={(e) => handleChange('subjectId', e.target.value)}
                          required
                        >
                          {subjects.map((s) => (
                            <MenuItem key={s._id} value={s._id}>
                              {s.name} (G{s.grade})
                            </MenuItem>
                          ))}
                        </TextField>
                        <TextField
                          select
                          label="Grade"
                          value={form.grade}
                          onChange={(e) => handleChange('grade', e.target.value)}
                          required
                        >
                          {[...new Set(subjects.map((s) => s.grade))].map((g) => (
                            <MenuItem key={g} value={g}>{`Grade ${g}`}</MenuItem>
                          ))}
                        </TextField>
                        <TextField
                          select
                          label="Section (optional)"
                          value={form.sectionId}
                          onChange={(e) => handleChange('sectionId', e.target.value)}
                        >
                          <MenuItem value="">All sections</MenuItem>
                          {sections
                            .filter((sec) => !form.grade || Number(sec.grade) === Number(form.grade))
                            .map((sec) => (
                              <MenuItem key={sec._id} value={sec._id}>
                                {`${sec.year}-G${sec.grade}${sec.section}`}
                              </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                          label="Video URL (YouTube/MP4)"
                          value={form.videoUrl}
                          onChange={(e) => handleChange('videoUrl', e.target.value)}
                        />
                        <TextField
                          label="Upload video (MP4)"
                          type="file"
                          inputProps={{ accept: 'video/*' }}
                          onChange={handleVideoSelect}
                        />
                        {form.videoFile && (
                          <Typography variant="caption" color="text.secondary">
                            Selected video: {form.videoFile.name}
                          </Typography>
                        )}
                        {editingLesson?.videoFile?.originalName && !form.videoFile && (
                          <Typography variant="caption" color="text.secondary">
                            Existing video: {editingLesson.videoFile.originalName}
                          </Typography>
                        )}
                        <TextField
                          label="Attach PDFs/Notes"
                          type="file"
                          inputProps={{ multiple: true }}
                          onChange={handleFileSelect}
                        />
                        {editingLesson?.files?.length > 0 && (
                          <Stack spacing={1}>
                            <Typography variant="caption" color="text.secondary">
                              Existing files
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap">
                              {editingLesson.files.map((file) => (
                                <Chip key={file.key} size="small" label={file.originalName} />
                              ))}
                            </Stack>
                          </Stack>
                        )}
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                          <TextField
                            label="Available from"
                            type="datetime-local"
                            value={form.availabilityStart || ''}
                            onChange={(e) => handleChange('availabilityStart', e.target.value)}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                          />
                          <TextField
                            label="Available until"
                            type="datetime-local"
                            value={form.availabilityEnd || ''}
                            onChange={(e) => handleChange('availabilityEnd', e.target.value)}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                          />
                        </Stack>
                        <Divider />
                        <Stack spacing={1}>
                          <Typography variant="subtitle1" fontWeight={700}>
                            Interactive quiz
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Add MCQ or short-answer questions for learners to complete.
                          </Typography>
                        </Stack>
                        <Stack spacing={2}>
                          {form.quizQuestions.length === 0 && (
                            <Typography variant="body2" color="text.secondary">
                              No questions yet. Add your first quiz question below.
                            </Typography>
                          )}
                          {form.quizQuestions.map((question, index) => (
                            <Card key={`${question.type}-${index}`} variant="outlined" sx={{ borderRadius: 2 }}>
                              <CardContent sx={{ p: 2 }}>
                                <Stack spacing={2}>
                                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                                    <TextField
                                      select
                                      label="Question type"
                                      value={question.type}
                                      onChange={(e) => updateQuestion(index, { type: e.target.value })}
                                      sx={{ minWidth: 160 }}
                                    >
                                      <MenuItem value="mcq">MCQ</MenuItem>
                                      <MenuItem value="short">Short answer</MenuItem>
                                    </TextField>
                                    <Button color="error" variant="outlined" onClick={() => removeQuestion(index)}>
                                      Remove
                                    </Button>
                                  </Stack>
                                  <TextField
                                    label={`Question ${index + 1}`}
                                    value={question.prompt || ''}
                                    onChange={(e) => updateQuestion(index, { prompt: e.target.value })}
                                    multiline
                                    minRows={2}
                                  />
                                  {question.type === 'mcq' ? (
                                    <Stack spacing={1.5}>
                                      <Typography variant="caption" color="text.secondary">
                                        Options
                                      </Typography>
                                      {(question.options || []).map((option, optIndex) => (
                                        <Stack key={`${index}-opt-${optIndex}`} direction="row" spacing={1}>
                                          <TextField
                                            label={`Option ${optIndex + 1}`}
                                            value={option}
                                            onChange={(e) => updateOption(index, optIndex, e.target.value)}
                                            fullWidth
                                          />
                                          <Button
                                            color="error"
                                            variant="outlined"
                                            onClick={() => removeOption(index, optIndex)}
                                          >
                                            Remove
                                          </Button>
                                        </Stack>
                                      ))}
                                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                        <Button variant="outlined" onClick={() => addOption(index)}>
                                          Add option
                                        </Button>
                                        <TextField
                                          select
                                          label="Correct option"
                                          value={question.correctIndex ?? 0}
                                          onChange={(e) => updateQuestion(index, { correctIndex: Number(e.target.value) })}
                                          sx={{ minWidth: 200 }}
                                        >
                                          {(question.options || []).map((_opt, optIndex) => (
                                            <MenuItem key={`${index}-correct-${optIndex}`} value={optIndex}>
                                              Option {optIndex + 1}
                                            </MenuItem>
                                          ))}
                                        </TextField>
                                      </Stack>
                                    </Stack>
 ) : (
                                    <TextField
                                      label="Suggested answer (optional)"
                                      value={question.answer || ''}
                                      onChange={(e) => updateQuestion(index, { answer: e.target.value })}
                                      multiline
                                      minRows={2}
                                    />
                                  )}
                                </Stack>
                              </CardContent>
                            </Card>
                          ))}
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                            <Button variant="outlined" onClick={() => addQuestion('mcq')}>
                              Add MCQ
                            </Button>
                            <Button variant="outlined" onClick={() => addQuestion('short')}>
                              Add short answer
                            </Button>
                          </Stack>
                        </Stack>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={form.isVisible}
                              onChange={(e) => handleChange('isVisible', e.target.checked)}
                            />
                          }
                          label="Visible to learners"
                        />
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                          <Button
                            type="submit"
                            variant="contained"
                            startIcon={<PublishIcon />}
                            disabled={submitting}
                          >
 {editingLessonId ? 'Save changes' : 'Publish lesson'}
                          </Button>
                          {editingLessonId && (
                            <Button variant="outlined" onClick={resetForm} disabled={submitting}>
                              Cancel edit
                            </Button>
                          )}
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={5}>
                  <Card elevation={1} sx={{ borderRadius: 3, height: '100%' }}>
                    <CardHeader title="Recently published" />
                    <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                      <Stack spacing={2}>
                        {lessons.length === 0 && (
                          <Typography variant="body2" color="text.secondary">
                            No lessons yet. Publish your first lesson to see it here.
                          </Typography>
                        )}
                        {lessons.map((lesson) => {
                          const lessonSubject = subjects.find((s) => s._id === lesson.subjectId);
                          return (
                            <Card key={lesson._id} elevation={0} variant="outlined" sx={{ borderRadius: 2 }}>
                              <CardContent>
                                <Stack spacing={1}>
                                  <Stack direction="row" alignItems="center" spacing={1}>
                                    <Typography fontWeight={700}>{lesson.title}</Typography>
                                    <Chip size="small" label={`G${lesson.grade}`} />
                                    {lesson.isVisible ? (
                                      <Chip size="small" color="success" label="Visible" />
 ) : (
                                      <Chip size="small" color="default" label="Hidden" />
                                    )}
                                  </Stack>
                                  <Typography variant="body2" color="text.secondary">
                                    {lesson.description || 'No description'}
                                  </Typography>
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    divider={<Divider flexItem orientation="vertical" />}
                                  >
                                    <Typography variant="caption">
                                      Subject: {lessonSubject?.name || lesson.subjectId}
                                    </Typography>
                                    <Typography variant="caption">Files: {lesson.files?.length || 0}</Typography>
                                  </Stack>
                                  <Stack direction="row" spacing={1}>
                                    <Button size="small" variant="outlined" onClick={() => loadLessonToForm(lesson, 'edit')}>
                                      Edit
                                    </Button>
                                    <Button size="small" variant="outlined" onClick={() => loadLessonToForm(lesson, 'reuse')}>
                                      Reuse
                                    </Button>
                                  </Stack>
                                </Stack>
                              </CardContent>
                            </Card>
                          );
                        })}
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    </EducatorLayout>
  );
};

export default EducatorLessons;
